import { formatCentAmount, formatWholeAmount } from '@/lib/campaigns/paid-money';

/**
 * Evidence floors for the Campaigns list and detail (board ruling EVIDENCE-FLOORS, signed 2026-10-21).
 *
 * Mora shows evidence by default. "Too early to judge", Raise and Stop appear only when every gate passes,
 * computed here from the person's own target, with a one-line basis. No threshold is ever printed: the
 * copy below the floor carries the campaign's own figures and never a value from FLOOR_CONFIG. Unknown
 * (`null`) is never read as zero, and unknown never passes a gate.
 *
 * Every value in FLOOR_CONFIG is configurable and carries a `source` tag. None is provider-sourced; a
 * verified figure replaces a placeholder here without a rebuild. Meta's learning-event figure is
 * unverified and is deliberately not used.
 */

export type FloorSource =
  | 'operator reference (marketer.com), unverified'
  | 'seat judgment, unverified'
  | 'seat judgment, unverified, confidence 0.3'
  | 'placeholder, the ruling gives no figure'
  | 'Mora product choice'
  | 'board ruling REVIEW-AGENT-LOOP item 3, unverified'
  | `https://${string}`;

export interface FloorValue {
  value: number;
  source: FloorSource;
}

export interface EvidenceFloorConfig {
  /** Results of the campaign's own measure before a verdict is given. */
  verdictResults: FloorValue;
  /** Full days delivered before a verdict is given. */
  verdictDays: FloorValue;
  /** Raise size, as a fraction of the current daily amount. */
  raiseStep: FloorValue;
  /** Days that must pass between two raises. */
  raiseSpacingDays: FloorValue;
  /** Days without a significant edit (budget, audience, creative, bid) before any judgement. */
  noEditDays: FloorValue;
  /** Stop needs spend of at least this multiple of the person's target (also the "far worse" cost multiple). */
  stopSpendMultiple: FloorValue;
  /** Days delivered before Stop is offered. */
  stopDays: FloorValue;
  /** Window for thin measures (bookings), which only get a verdict with a target set. */
  thinVerdictDays: FloorValue;
  /** A suggested daily amount at or below this (minor units) goes on one channel. Never printed. */
  oneChannelAtOrBelowMinor: FloorValue;
  /** The smallest daily amount Mora puts on LinkedIn (minor units). Mora's choice, not LinkedIn's figure. */
  linkedInMinimumDailyMinor: FloorValue;
  /** Days both ads must have delivered together before one is called ahead or behind. Words only, never printed. */
  adComparisonTogetherDays: FloorValue;
  /** Results the weaker ad must have counted before it is called behind. */
  adComparisonWeakerResults: FloorValue;
  /** How much worse (as a fraction) the weaker ad's cost per result must be before a new version is proposed. */
  adComparisonCostGap: FloorValue;
  /** Consecutive daily readouts that must show that gap before a new version is proposed. */
  adComparisonReadoutsInARow: FloorValue;
  /**
   * Days old the last sync, or the newest daily figure of a campaign that is still running, may be before the daily
   * review reads its figures as out of date and proposes nothing. Never printed as a threshold.
   */
  staleDataDays: FloorValue;
}

export const FLOOR_CONFIG: EvidenceFloorConfig = {
  verdictResults: { value: 25, source: 'operator reference (marketer.com), unverified' },
  verdictDays: { value: 7, source: 'operator reference (marketer.com), unverified' },
  raiseStep: { value: 0.2, source: 'operator reference (marketer.com), unverified' },
  raiseSpacingDays: { value: 7, source: 'seat judgment, unverified' },
  noEditDays: { value: 7, source: 'seat judgment, unverified' },
  stopSpendMultiple: { value: 3, source: 'seat judgment, unverified, confidence 0.3' },
  stopDays: { value: 7, source: 'seat judgment, unverified' },
  thinVerdictDays: { value: 28, source: 'placeholder, the ruling gives no figure' },
  oneChannelAtOrBelowMinor: { value: 1500, source: 'Mora product choice' },
  linkedInMinimumDailyMinor: { value: 1000, source: 'Mora product choice' },
  adComparisonTogetherDays: {
    value: 7,
    source: 'board ruling REVIEW-AGENT-LOOP item 3, unverified',
  },
  adComparisonWeakerResults: {
    value: 9,
    source: 'board ruling REVIEW-AGENT-LOOP item 3, unverified',
  },
  adComparisonCostGap: { value: 0.3, source: 'board ruling REVIEW-AGENT-LOOP item 3, unverified' },
  adComparisonReadoutsInARow: {
    value: 2,
    source: 'board ruling REVIEW-AGENT-LOOP item 3, unverified',
  },
  staleDataDays: { value: 3, source: 'seat judgment, unverified' },
};

export type EvidenceChannel = 'meta' | 'google' | 'linkedin';
/** `thin` is a measure expected to stay under the verdict count in 30 days (bookings). */
export type MeasureClass = 'standard' | 'thin';
export type ProviderLearningState = 'ready' | 'learning' | 'limited' | null;

export interface EvidenceInput {
  channel: EvidenceChannel;
  measureClass: MeasureClass;
  /** True only when Mora created the campaign. */
  createdByMora: boolean;
  /** True when the figures are the campaign's own chosen measure, not a stand-in. */
  figuresAreOwnMeasure: boolean;
  /** True when tracking for the chosen measure is confirmed to fire; null is not known. */
  trackingConfirmed: boolean | null;
  providerState: ProviderLearningState;
  /** Days since the last significant edit; null is not known. */
  daysSinceLastEdit: number | null;
  /** Results of the chosen measure; null is not read, which is never zero. */
  results: number | null;
  spend: number | null;
  daysDelivered: number | null;
  /** The person's own cost target per result; null when none is on file. */
  target: number | null;
  /** Reported by the provider; null is not known. */
  budgetLimited: boolean | null;
  dailyAmount: number | null;
  daysSinceLastRaise: number | null;
  /** Null when the trend cannot be read. */
  trendImproving: boolean | null;
  currency: string;
  /** Singular noun of the measure: "order", "lead", "booking". */
  measureNoun?: string;
}

export type GateCode =
  | 'not_created_by_mora'
  | 'not_own_measure'
  | 'tracking_unconfirmed'
  | 'provider_learning'
  | 'recent_edit';

export type GateResult = { pass: true } | { pass: false; code: GateCode; copy: string };

const PASS: GateResult = { pass: true };

/** Words only; none of these carries a value from FLOOR_CONFIG. */
export const GATE_COPY: Readonly<Record<GateCode, string>> = {
  not_created_by_mora:
    'No move yet. Mora did not create this campaign, so it shows the figures only.',
  not_own_measure:
    'No move yet. These figures are not for the result you chose, so Mora cannot judge them.',
  tracking_unconfirmed: 'No results are being counted yet. Check that results are being counted.',
  provider_learning:
    'No move yet. The channel is still learning, so Mora is not judging this campaign.',
  recent_edit:
    'No move yet. This campaign was changed recently, so Mora will wait for a full week of delivery before judging it.',
};

export function gateCreatedByMora(input: Pick<EvidenceInput, 'createdByMora'>): GateResult {
  return input.createdByMora
    ? PASS
    : { pass: false, code: 'not_created_by_mora', copy: GATE_COPY.not_created_by_mora };
}

export function gateOwnMeasure(input: Pick<EvidenceInput, 'figuresAreOwnMeasure'>): GateResult {
  return input.figuresAreOwnMeasure
    ? PASS
    : { pass: false, code: 'not_own_measure', copy: GATE_COPY.not_own_measure };
}

/** Unknown tracking does not pass: only a confirmed measure can be judged. */
export function gateTracking(input: Pick<EvidenceInput, 'trackingConfirmed'>): GateResult {
  return input.trackingConfirmed === true
    ? PASS
    : { pass: false, code: 'tracking_unconfirmed', copy: GATE_COPY.tracking_unconfirmed };
}

/** Only a provider that reports "ready" passes; learning, limited and not known all hold judgement. */
export function gateProviderState(input: Pick<EvidenceInput, 'providerState'>): GateResult {
  return input.providerState === 'ready'
    ? PASS
    : { pass: false, code: 'provider_learning', copy: GATE_COPY.provider_learning };
}

export function gateNoRecentEdit(
  input: Pick<EvidenceInput, 'daysSinceLastEdit'>,
  config: EvidenceFloorConfig = FLOOR_CONFIG,
): GateResult {
  const days = input.daysSinceLastEdit;
  return days !== null && days >= config.noEditDays.value
    ? PASS
    : { pass: false, code: 'recent_edit', copy: GATE_COPY.recent_edit };
}

export type JudgementState = 'evidence_only' | 'too_early' | 'verdict';

export type TooEarlyCode =
  'not_read' | 'too_few_results' | 'thin_measure' | 'linkedin_evidence_only';

export interface RaiseMove {
  eligible: boolean;
  /** Always confirmed by the person; Mora never writes a Raise on its own. */
  requiresConfirmation: true;
  copy: string;
  newDailyAmount: number | null;
}

export type StopKind = 'stop' | 'pause_and_review' | 'check_tracking';

export interface StopMove {
  eligible: boolean;
  kind: StopKind | null;
  /** True for Stop: the person types to confirm. */
  requiresTypedConfirmation: boolean;
  copy: string;
}

export interface Judgement {
  state: JudgementState;
  /** The gate that held judgement back, when one did. */
  gate: GateCode | null;
  reason: TooEarlyCode | 'no_target' | null;
  /** One line stating why this state, in words. */
  basis: string;
  raise: RaiseMove;
  stop: StopMove;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function days(count: number): string {
  return plural(count, 'day');
}

function noun(input: EvidenceInput): string {
  return input.measureNoun ?? 'result';
}

const NO_RAISE = (copy: string): RaiseMove => ({
  eligible: false,
  requiresConfirmation: true,
  copy,
  newDailyAmount: null,
});

const NO_STOP = (copy: string, kind: StopKind | null = null): StopMove => ({
  eligible: false,
  kind,
  requiresTypedConfirmation: false,
  copy,
});

export const NO_TARGET_COPY =
  'Tell Mora what one result is worth to you, and it can say whether the cost is working.';

export function tooEarlyCopy(input: EvidenceInput): string {
  const n = noun(input);
  if (input.results === null || input.daysDelivered === null) {
    return `Mora has not read the ${n} count yet, so it cannot judge cost per ${n}.`;
  }
  return `${plural(input.results, n)} in ${days(input.daysDelivered)}. That is too few to judge cost per ${n}. Mora will give a view once there are more ${n}s and a full week of delivery since the last change.`;
}

export const THIN_MEASURE_COPY =
  'This kind of campaign collects few results. Compare the trend, not a single cost.';

export function raiseNotYetCopy(): string {
  return 'No move yet. We would suggest more budget once there are enough results to trust the cost.';
}

export function stopNotYetCopy(input: EvidenceInput): string {
  const results = input.results === null ? 'no count read' : plural(input.results, noun(input));
  const elapsed = input.daysDelivered === null ? 'an unknown time' : days(input.daysDelivered);
  return `No move yet. ${elapsed} and ${results} so far, not enough to say it is not working.`;
}

function costPerResult(input: EvidenceInput): number | null {
  if (input.spend === null || input.results === null || input.results <= 0) return null;
  return input.spend / input.results;
}

function learningNote(input: Pick<EvidenceInput, 'channel' | 'providerState'>): string {
  if (input.channel === 'meta') return 'Meta says this ad set is limited in learning';
  if (input.providerState === 'learning' || input.providerState === 'limited')
    return 'Google Ads is still learning';
  return 'Mora has not read whether Google Ads is still learning';
}

/**
 * Stop when the campaign is far over its own target. With `pauseReason` (a gate holds the judgement, but the
 * money already spent is far over target), it is "pause and review" instead, with no typed confirmation, and
 * the sentence says why Mora is not calling it a verdict.
 */
function evaluateStop(
  input: EvidenceInput,
  config: EvidenceFloorConfig,
  pauseReason: string | null,
): StopMove {
  const target = input.target;
  const eligibleWindow =
    input.daysDelivered !== null && input.daysDelivered >= config.stopDays.value;
  if (target === null || target <= 0 || input.spend === null || input.results === null) {
    return NO_STOP(stopNotYetCopy(input));
  }
  const meaningfulSpend = input.spend >= target * config.stopSpendMultiple.value;
  const cost = costPerResult(input);
  const farWorse =
    input.results === 0 || (cost !== null && cost >= target * config.stopSpendMultiple.value);
  if (!eligibleWindow || !meaningfulSpend || !farWorse || input.trendImproving === true) {
    return NO_STOP(stopNotYetCopy(input));
  }
  const spent = formatWholeAmount(input.spend, input.currency);
  const goal = formatCentAmount(target, input.currency);
  const n = noun(input);
  const elapsed = days(input.daysDelivered as number);
  const body =
    input.results === 0
      ? `Spent ${spent} over ${elapsed} with no ${n}s counted.`
      : `Spent ${spent} over ${elapsed} for ${plural(input.results, n)}, ${formatCentAmount(cost, input.currency)} each.`;
  if (pauseReason !== null) {
    return {
      eligible: true,
      kind: 'pause_and_review',
      requiresTypedConfirmation: false,
      copy: `${body} ${pauseReason}`,
    };
  }
  return {
    eligible: true,
    kind: 'stop',
    requiresTypedConfirmation: true,
    copy: `${body} Your goal was ${goal} per ${n}. You can stop it or leave it.`,
  };
}

function evaluateRaise(input: EvidenceInput, config: EvidenceFloorConfig): RaiseMove {
  const cost = costPerResult(input);
  if (input.target === null || input.target <= 0) return NO_RAISE(NO_TARGET_COPY);
  if (
    cost === null ||
    cost > input.target ||
    input.budgetLimited !== true ||
    input.dailyAmount === null ||
    (input.daysSinceLastRaise !== null && input.daysSinceLastRaise < config.raiseSpacingDays.value)
  ) {
    return NO_RAISE(raiseNotYetCopy());
  }
  const newDailyAmount = input.dailyAmount * (1 + config.raiseStep.value);
  const pct = Math.round(config.raiseStep.value * 100);
  return {
    eligible: true,
    requiresConfirmation: true,
    newDailyAmount,
    copy: `Cost per ${noun(input)} is ${formatCentAmount(cost, input.currency)} against your ${formatCentAmount(input.target, input.currency)}, over ${days(input.daysDelivered ?? 0)}. Raising by ${pct}% would be ${formatCentAmount(newDailyAmount, input.currency)} a day.`,
  };
}

/**
 * The one judgement of a campaign. Evidence by default; the common gates run first and any failure holds
 * Raise and Stop back, with the ruled copy. Tracking that is not confirmed gives "check that results are
 * being counted" and never Stop.
 */
export function judgeCampaign(
  input: EvidenceInput,
  config: EvidenceFloorConfig = FLOOR_CONFIG,
): Judgement {
  const gates: GateResult[] = [
    gateCreatedByMora(input),
    gateOwnMeasure(input),
    gateTracking(input),
    gateProviderState(input),
    gateNoRecentEdit(input, config),
  ];
  // At small budgets Meta's "learning limited" is the normal state, and Mora does not read Google's learning
  // state at all. Holding every judgement for either would mean a campaign far over target is never paused, so
  // both allow "pause and review" (never a full Stop) when learning is the only gate that failed.
  const learningOnGoogle =
    (input.channel === 'google' && input.providerState !== 'ready') ||
    (input.channel === 'meta' && input.providerState === 'limited');
  const failed = gates.find((g): g is Extract<GateResult, { pass: false }> => !g.pass);

  if (failed) {
    // Spend far over target with nothing counted is either broken tracking or a dead ad; spend far over target
    // while the channel is learning is still money spent. Neither is a verdict, but both may be paused.
    const failing = gates.filter((g): g is Extract<GateResult, { pass: false }> => !g.pass);
    const nothingCounted = input.results === 0;
    const pausable = failing.every(
      (g) =>
        (g.code === 'provider_learning' && learningOnGoogle) ||
        (g.code === 'tracking_unconfirmed' && nothingCounted),
    );
    const pauseReason = failing.some((g) => g.code === 'tracking_unconfirmed')
      ? `Check that ${noun(input)}s are being counted; you can pause it and review.`
      : `${learningNote(input)}, so you can pause it and review.`;
    const paused = pausable ? evaluateStop(input, config, pauseReason) : null;
    const stop =
      paused && paused.eligible
        ? paused
        : NO_STOP(failed.copy, failed.code === 'tracking_unconfirmed' ? 'check_tracking' : null);
    return {
      state: 'evidence_only',
      gate: failed.code,
      reason: null,
      basis: failed.copy,
      raise: NO_RAISE(failed.copy),
      stop,
    };
  }

  if (input.channel === 'linkedin') {
    return {
      state: 'evidence_only',
      gate: null,
      reason: 'linkedin_evidence_only',
      basis: THIN_MEASURE_COPY,
      raise: NO_RAISE(raiseNotYetCopy()),
      stop: NO_STOP(stopNotYetCopy(input)),
    };
  }

  const hasTarget = input.target !== null && input.target > 0;

  if (input.measureClass === 'thin') {
    const longEnough =
      input.daysDelivered !== null && input.daysDelivered >= config.thinVerdictDays.value;
    const stop = hasTarget ? evaluateStop(input, config, null) : NO_STOP(NO_TARGET_COPY);
    if (hasTarget && longEnough) {
      return {
        state: 'verdict',
        gate: null,
        reason: null,
        basis: `${plural(input.results ?? 0, noun(input))} over ${days(input.daysDelivered as number)} against your target.`,
        raise: NO_RAISE(raiseNotYetCopy()),
        stop,
      };
    }
    return {
      state: 'evidence_only',
      gate: null,
      reason: hasTarget ? 'thin_measure' : 'no_target',
      basis: hasTarget ? THIN_MEASURE_COPY : NO_TARGET_COPY,
      raise: NO_RAISE(raiseNotYetCopy()),
      stop,
    };
  }

  const verdictMet =
    input.results !== null &&
    input.daysDelivered !== null &&
    input.results >= config.verdictResults.value &&
    input.daysDelivered >= config.verdictDays.value;
  const stop = hasTarget ? evaluateStop(input, config, null) : NO_STOP(NO_TARGET_COPY);

  if (!verdictMet && !stop.eligible) {
    const notRead = input.results === null || input.daysDelivered === null;
    return {
      state: 'too_early',
      gate: null,
      reason: notRead ? 'not_read' : 'too_few_results',
      basis: tooEarlyCopy(input),
      raise: NO_RAISE(hasTarget ? raiseNotYetCopy() : NO_TARGET_COPY),
      stop,
    };
  }

  const raise = verdictMet ? evaluateRaise(input, config) : NO_RAISE(raiseNotYetCopy());
  const cost = costPerResult(input);
  const basis =
    cost !== null
      ? `Cost per ${noun(input)} is ${formatCentAmount(cost, input.currency)} over ${days(input.daysDelivered ?? 0)}, ${plural(input.results ?? 0, noun(input))} counted.`
      : `${plural(input.results ?? 0, noun(input))} counted over ${days(input.daysDelivered ?? 0)}.`;

  return {
    state: 'verdict',
    gate: null,
    reason: hasTarget ? null : 'no_target',
    basis,
    raise: hasTarget ? raise : NO_RAISE(NO_TARGET_COPY),
    stop,
  };
}

/**
 * The no-unsourced-figure guard for copy about budgets (board ruling GOOGLE-PURCHASES-AND-SMALL-BUDGETS, B).
 * Returns every number in `text` that is not one of `allowed` (the figures the sentence was built from:
 * the person's amount, the weekly spend, the recorded cost, the derived purchase count). A learning-phase
 * threshold, a provider minimum Mora has not read, or any literal in the copy shows up here. Money is read
 * without its symbol and thousands separators, so `$1,200` is `1200`.
 */
export function unsourcedFigures(text: string, allowed: readonly (string | number)[]): string[] {
  const known = new Set(allowed.map((value) => String(value)));
  const found = text.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? [];
  return found.filter((figure) => !known.has(figure));
}
